import { useState } from "react";
import { saveSection, defaultHtml } from "./sectionStore";

const C = {
  surface:"#181818", surface2:"#202020", border:"#2a2a2a", muted:"#666",
  orange:"#FF5F1F", magenta:"#E8197D", green:"#4ade80", white:"#fff", red:"#f87171", blue:"#60a5fa",
};
const card = (x={}) => ({ background:C.surface, borderRadius:10, padding:16, border:`0.5px solid ${C.border}`, ...x });
const inp  = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const btn  = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white}:{}), ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...(v==="danger"?{background:"#3d1515",color:C.red,border:`0.5px solid #f8717133`}:{}), ...x });
const lbl  = t => <div style={{fontSize:11,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>{t}</div>;

// ---- HTML-felt-hjelpere (DOMParser) ----
// Dynamiske data-bind-felt vises som lesbare tokens i Enkel-modus, så f.eks.
// kundenavnet vises som {kunde} (ikke flat tekst) og bindingen bevares ved lagring.
const TOKENS = { customer:"{kunde}", price:"{pris}", delta:"{rabatt}" };
const TOKEN_TO_BIND = { "{kunde}":"customer", "{pris}":"price", "{rabatt}":"delta" };
const TOKEN_RE = /(\{kunde\}|\{pris\}|\{rabatt\})/;

const parseRoot = html => new DOMParser().parseFromString(html, "text/html").body.firstElementChild;
const pickEl = (root, kind) => {
  if(!root) return null;
  if(kind==="eyebrow") return root.querySelector('[class*="eyebrow"] span:last-child');
  if(kind==="intro")   return root.querySelector('.intro, .hero-sub, .cta-sub');
  return null;
};
// Element -> tekst der data-bind-felt blir {tokens}
function elToTemplate(el) {
  let out = "";
  el.childNodes.forEach(n => {
    if (n.nodeType === 3) out += n.textContent;
    else if (n.nodeType === 1) {
      const b = n.getAttribute("data-bind");
      out += (b && TOKENS[b]) ? TOKENS[b] : elToTemplate(n);
    }
  });
  return out;
}
// Skriv tekst (m/tokens) tilbake i element; gjenbruk eksisterende data-bind-spans
function templateToEl(el, value) {
  const existing = {};
  el.querySelectorAll("[data-bind]").forEach(b => {
    const k = b.getAttribute("data-bind"); if (!existing[k]) existing[k] = b.cloneNode(true);
  });
  el.textContent = "";
  value.split(TOKEN_RE).forEach(part => {
    if (!part) return;
    const bind = TOKEN_TO_BIND[part];
    if (bind) {
      let node = existing[bind];
      if (!node) { node = document.createElement("span"); node.setAttribute("data-bind", bind); node.textContent = bind==="customer" ? "Kunde" : ""; }
      el.appendChild(node.cloneNode(true));
    } else el.appendChild(document.createTextNode(part));
  });
}
const getField = (html, kind) => { const e = pickEl(parseRoot(html), kind); return e ? elToTemplate(e) : null; };
const setField = (html, kind, val) => { const r = parseRoot(html); const e = pickEl(r, kind); if(e) templateToEl(e, val); return r ? r.outerHTML : html; };

// Forhåndsvisning fyller data-bind med eksempelverdier (som på en ekte kundeside)
const previewDoc = html => `<!doctype html><html lang="nb"><head><meta charset="utf-8">
<link rel="stylesheet" href="https://app.royal.show/portal/styles.css">
<style>html,body{background:#0a0a0c;margin:0}.reveal{opacity:1!important;transform:none!important}</style>
</head><body>${html}
<script>
  document.querySelectorAll('[data-bind=customer]').forEach(function(e){e.textContent='Kundenavn';});
  document.querySelectorAll('[data-bind=price]').forEach(function(e){e.textContent='14 875';});
  document.querySelectorAll('[data-bind=delta]').forEach(function(e){e.textContent='−15 % under veiledende';});
</script>
</body></html>`;

function OneEditor({ section, onSaved, showToast }) {
  const [mode, setMode] = useState("simple"); // "simple" | "html"
  const [lang, setLang] = useState("no");     // "no" | "en"
  const [drafts, setDrafts] = useState({ no: section.html, en: section.html_en || "" });
  const [saving, setSaving] = useState(false);
  const html = drafts[lang];
  const setHtml = (v) => setDrafts(d => ({ ...d, [lang]: v }));
  const dirty = drafts.no !== section.html || drafts.en !== (section.html_en || "");

  const eyebrow = getField(html, "eyebrow");
  const intro   = getField(html, "intro");

  const save = async () => {
    setSaving(true);
    const err = await saveSection(section.key, { html: drafts.no, html_en: drafts.en || null, label: section.label });
    setSaving(false);
    if(err){ showToast("Kunne ikke lagre: "+err.message, false); return; }
    showToast(`«${section.label}» lagret.`);
    onSaved(section.key, { html: drafts.no, html_en: drafts.en || "" });
  };
  const reset = () => {
    if(lang==="en"){ if(window.confirm("Tøm den engelske versjonen?")) setHtml(""); return; }
    if(!window.confirm(`Tilbakestille «${section.label}» til standard?`)) return;
    setHtml(defaultHtml(section.key));
  };

  return (
    <div style={{...card({background:C.surface2}),marginTop:10}}>
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        {["simple","html"].map(m=>(
          <button key={m} onClick={()=>setMode(m)} style={{...btn(mode===m?"primary":"ghost"),padding:"5px 14px",fontSize:12}}>
            {m==="simple"?"Enkel":"Avansert (HTML)"}
          </button>
        ))}
        <div style={{marginLeft:"auto",display:"flex",gap:6}}>
          {[{c:"no",l:"Norsk"},{c:"en",l:"English"}].map(x=>(
            <button key={x.c} onClick={()=>setLang(x.c)} style={{...btn(lang===x.c?"primary":"ghost"),padding:"5px 12px",fontSize:12}}>{x.l}</button>
          ))}
        </div>
      </div>
      {lang==="en" && !html.trim() && (
        <div style={{marginBottom:10,fontSize:12,color:C.muted}}>
          Ingen engelsk versjon ennå. <button style={{...btn("ghost"),padding:"4px 10px",fontSize:12}} onClick={()=>setDrafts(d=>({...d,en:d.no}))}>Start fra norsk</button> og oversett, eller lim inn engelsk HTML i «Avansert».
        </div>
      )}

      {mode==="simple" ? (
        <div>
          {eyebrow!==null && (<><div>{lbl("Liten overskrift (eyebrow)")}</div>
            <input style={{...inp(),marginBottom:14}} value={eyebrow} onChange={e=>setHtml(setField(html,"eyebrow",e.target.value))}/></>)}
          {intro!==null && (<><div>{lbl("Ingress")}</div>
            <textarea style={{...inp(),marginBottom:6,minHeight:90,resize:"vertical",fontFamily:"inherit"}} value={intro} onChange={e=>setHtml(setField(html,"intro",e.target.value))}/></>)}
          {eyebrow===null && intro===null && (
            <div style={{fontSize:12,color:C.muted,marginBottom:6}}>Denne seksjonen har ingen enkle tekstfelt — bruk «Avansert (HTML)» for å redigere.</div>
          )}
          <div style={{fontSize:11,color:C.muted,lineHeight:1.5}}>
            <code style={{color:C.blue}}>{"{kunde}"}</code> fylles inn med kundenavnet automatisk
            (<code style={{color:C.blue}}>{"{pris}"}</code>/<code style={{color:C.blue}}>{"{rabatt}"}</code> likeså). Overskrifter med uthevet stil og resten av innholdet redigeres i «Avansert».
          </div>
        </div>
      ) : (
        <div>
          {lbl("Seksjonens HTML")}
          <textarea spellCheck={false} style={{...inp(),minHeight:240,resize:"vertical",fontFamily:"ui-monospace,monospace",fontSize:12,lineHeight:1.5}}
            value={html} onChange={e=>setHtml(e.target.value)}/>
        </div>
      )}

      <div style={{marginTop:14}}>{lbl("Forhåndsvisning")}</div>
      <iframe title={`preview-${section.key}`} srcDoc={previewDoc(html)}
        style={{width:"100%",height:360,border:`0.5px solid ${C.border}`,borderRadius:8,background:"#0a0a0c"}}/>

      <div style={{display:"flex",gap:8,marginTop:14,flexWrap:"wrap"}}>
        <button style={{...btn("primary"),opacity:(!dirty||saving)?0.5:1,pointerEvents:(!dirty||saving)?"none":"auto"}} onClick={save}>
          {saving?"Lagrer…":"Lagre seksjon"}
        </button>
        <button style={btn("ghost")} onClick={reset}>Tilbakestill til standard</button>
      </div>
    </div>
  );
}

export function SectionEditorView({ sections, onSaved, onBack, showToast }) {
  const [openKey, setOpenKey] = useState(null);
  return (
    <div style={{maxWidth:720}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
        <div style={{fontSize:20,fontWeight:500}}>Rediger standardseksjoner</div>
        <button style={{...btn("ghost"),padding:"6px 14px",fontSize:12}} onClick={onBack}>← Tilbake</button>
      </div>
      <div style={{fontSize:13,color:C.muted,marginBottom:20}}>
        Endringer her gjelder alle <strong>nye og re-publiserte</strong> portaler. Kundenavn og pris fylles
        fortsatt inn automatisk per kunde.
      </div>
      {sections.map(s=>(
        <div key={s.key} style={{...card(),marginBottom:10}}>
          <div onClick={()=>setOpenKey(openKey===s.key?null:s.key)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}>
            <div style={{fontSize:15,fontWeight:500}}>{s.label} <span style={{fontSize:12,color:C.muted}}>· {s.key}</span></div>
            <span style={{color:C.muted,fontSize:13}}>{openKey===s.key?"▲":"Rediger ▾"}</span>
          </div>
          {openKey===s.key && <OneEditor section={s} onSaved={onSaved} showToast={showToast}/>}
        </div>
      ))}
    </div>
  );
}
